import React from 'react';
import { t } from '@sm/intl';
import { Box } from '@wds/box';
import { Link } from '@wds/link';

import { Typography } from '@wds/typography';
import { usePricingState } from '~shared/hooks/usePricingState';
import { PackageIds, Packages, ROOT_PRICING_ROUTE } from '~shared/constants/pricing';
import { PricingPackage } from '~shared/types/pricing/pricingsvc';
import { TranslationsPlaceHolderType } from '~app/pages/Pricing/types';

import useSummaryStyles from '../Summary/useStyles';
import HEADING_COPY from '../copy/experimentHeadingCopy';
import MainHeading from '~pricing/components/MainHeading';
import Package from './components/Package';
import COPY from './copy';
import FOOTER_COPY from '~pricing/components/PricingFooter/copy';
import useStyles from './useStyles';
import { formatCostInLocaleCurrency } from '~app/pages/Pricing/lib/lib-helpers';
import useCta from '../hooks/useCta';

export default function PnPRefreshIPPM(): JSX.Element {
  const {
    state: {
      packages: { comparePlans, discountInfo, displayPackages, paidFeatures, overageCost, getPackageById },
    },
  } = usePricingState();

  const { Header, IPPMPackages } = useStyles();
  const { PackagesContainer } = useSummaryStyles();
  const { ctaUrl: contactSalesCtaUrl } = useCta(getPackageById(PackageIds[Packages.Enterprise]));

  const filteredPackages = displayPackages?.filter(
    pkg => ![Packages.Basic, Packages.Enterprise].includes(pkg?.packageName)
  );

  function getPackage(pricingPackage: PricingPackage, index: number): JSX.Element {
    const props = {
      ...pricingPackage,
      comparisonSkuCost: pricingPackage.comparisonSkuCost || [],
      comparePlans,
      discountInfo,
      key: `package-${pricingPackage.packageName}`,
      isLastPackage: index + 1 === filteredPackages.length,
      paidFeatures,
    };

    return <Package {...props} />;
  }

  function removeLink({ children }: Readonly<TranslationsPlaceHolderType>): JSX.Element {
    return (
      <Link
        href="#"
        onClick={() => {
          if (window) {
            window.parent.postMessage(
              {
                action: 'close_paywall',
              },
              window.location.origin
            );
          }
        }}
        rel="noreferrer"
      >
        {children}
      </Link>
    );
  }
  function educationLink({ children }: Readonly<TranslationsPlaceHolderType>): JSX.Element {
    return (
      <Link href={`${ROOT_PRICING_ROUTE}/educational/`} rel="noreferrer" target="_blank">
        {children}
      </Link>
    );
  }
  function contactLink({ children }: Readonly<TranslationsPlaceHolderType>): JSX.Element {
    return (
      <Link href={contactSalesCtaUrl} rel="noreferrer" target="_blank">
        {children}
      </Link>
    );
  }

  return (
    <>
      <header className={Header}>
        <MainHeading variant="pageTitle">{t(HEADING_COPY.CHOOSE_PLAN)}</MainHeading>
        <Typography component="p" align="center" variant="bodySm">
          {t(
            { ...COPY.UPGRADE },
            {
              returnBackToSurvey: removeLink,
              education: educationLink,
            }
          )}
        </Typography>
        <Typography component="p" align="center" variant="bodySm">
          {t(
            { ...COPY.ENTERPRISE },
            {
              contact: contactLink,
            }
          )}
        </Typography>
      </header>
      <Box className={IPPMPackages}>
        <Box className={PackagesContainer}>
          {filteredPackages?.map(
            (pricingPackage, index: number) => pricingPackage && getPackage(pricingPackage, index)
          )}
        </Box>
        {overageCost && (
          <Typography variant="bodySm" align="center">
            {t(FOOTER_COPY.PER_ADDITIONAL_RESPONSE, {
              overageCost: formatCostInLocaleCurrency(overageCost, 2),
            })}
          </Typography>
        )}
      </Box>
    </>
  );
}
